"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "@/lib/utils/cn";

export const buttonVariants = cva(
  "btn inline-flex items-center justify-center gap-ds-2 whitespace-nowrap font-sans font-semibold transition-[transform,box-shadow,filter,border-color,background-color,color] duration-base ease-standard disabled:pointer-events-none disabled:opacity-45",
  {
    variants: {
      variant: {
        primary: "btn-primary",
        secondary: "btn-secondary",
        ghost: "btn-ghost",
        danger: "btn-danger",
      },
      size: {
        sm: "btn-sm min-h-[var(--density-control-sm)] rounded-control px-ds-3 py-ds-2 text-caption",
        md: "min-h-[var(--density-control-md)] rounded-control px-ds-4 py-ds-2.5 text-caption",
        lg: "min-h-[var(--density-control-lg)] rounded-control px-ds-5 py-ds-3 text-body",
        icon: "btn-icon rounded-control",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    /** Retained for source compatibility. Buttons render as native buttons. */
    asChild?: boolean;
    loading?: boolean;
    loadingText?: string;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type = "button", loading = false, loadingText, children, disabled, asChild: _asChild, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden /> : null}
      {loading && loadingText ? loadingText : children}
    </button>
  );
});

export type IconButtonProps = Omit<ButtonProps, "size"> & {
  "aria-label": string;
  size?: "sm" | "md";
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { size = "sm", className, ...props },
  ref,
) {
  return (
    <Button
      ref={ref}
      size="icon"
      className={cn(size === "md" && "!h-10 !w-10 !min-h-10 !min-w-10", className)}
      {...props}
    />
  );
});
